import { cn, getCurrentRole, isSuperuser } from '@/lib/utils.ts'
import { NavigationGroupT, NavItemsFooNamesT } from '@/lib/types/navigationTypes.ts'
import NavigationGroupItemLink, { NavigationItemLinkT } from './NavigationGroupItemLink.tsx'
import NavigationGroupItemButton, {
  NavigationButtonT,
} from '@/components/common/menu/NavigationGroupItemButton.tsx'
import { useTranslation } from 'react-i18next'
import NavLangPopover from '@/components/common/menu/NavLangPopover.tsx'
import NavOfflineModeToggle from '@/components/common/menu/NavOfflineModeToggle.tsx'
import { Fragment } from 'react'
import { DesktopMenuLayerT, MobileMenuLayerT } from '@/store/useMenuStore.ts'

type PropsT = {
  navGroup: NavigationGroupT
  functionDecider: (functionName: NavItemsFooNamesT) => void | (() => void)
  className?: string
  menuLayer?: DesktopMenuLayerT | MobileMenuLayerT
}

export default function NavigationGroupsGroup({
  navGroup,
  functionDecider,
  className,
  menuLayer,
}: PropsT) {
  const { t } = useTranslation()
  const role = getCurrentRole()
  const isSpecialUser = isSuperuser()

  const allowedLinks = navGroup.items.filter(item => {
    // Check role-based access
    const hasRoleAccess = !item.allowedRoles || (role && item.allowedRoles.includes(role))
    // Check special user access
    const hasSpecialUserAccess = !item.specialUserOnly || isSpecialUser

    return hasRoleAccess && hasSpecialUserAccess
  })

  // On the Więcej bottom sheet (primaryMobile) the section padding and the
  // top-border between groups visually fragment a short list. Drop both for
  // a more compact, continuous menu feel.
  const isPrimaryMobile = menuLayer === 'primaryMobile'

  return (
    <section
      className={cn(
        'flex flex-col justify-center px-2 md:px-4',
        isPrimaryMobile ? 'py-0.5' : 'min-h-[3.75rem] border-t py-2',
        className,
      )}
    >
      {navGroup.title && (
        <header className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
          {navGroup.title}
        </header>
      )}
      <ul className={`h-full`}>
        {allowedLinks.map((item, index) => {
          if (item.name === t(`navigation.settings.users.language`)) {
            return (
              <Fragment key={index}>
                <NavLangPopover label={t(`navigation.settings.users.language`)} />
              </Fragment>
            )
          }

          // Offline mode lives in its own trailing group, but render-wise
          // it's still the Switch-based NavOfflineModeToggle component. The
          // nav item carries `function: 'offlineModeToggle'` as a dispatch
          // marker — same pattern as `changeLang` → NavLangPopover above.
          if (item.function === 'offlineModeToggle') {
            return <NavOfflineModeToggle key={index} />
          }

          if (item.link)
            return <NavigationGroupItemLink key={index} item={item as NavigationItemLinkT} />

          if (item.function)
            return (
              <NavigationGroupItemButton
                key={index}
                item={item as NavigationButtonT}
                functionDecider={functionDecider}
              />
            )
        })}
      </ul>
    </section>
  )
}
